import React from 'react';
import { ICustomSelectChips, ICustomSelectChipsWrapper } from './interface';
import { IOptionData } from 'interfaces/utils';
import {
  Grid,
  Select,
  Chip,
  MenuItem,
  FormControl,
  FormHelperText,
} from '@material-ui/core';
import { useFormikContext, ErrorMessage } from 'formik';

const CustomSelectChipsWrapper: React.FC<ICustomSelectChipsWrapper> = ({
  fieldName,
  ...rest
}) => {
  const { setFieldValue, getFieldMeta, setFieldTouched } = useFormikContext();
  const { touched, error, value } = getFieldMeta(fieldName);
  const rawSelectedItems = value;

  const onBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    rest.selectProps?.onBlur && rest.selectProps?.onBlur(event);
    setFieldTouched(fieldName, true);
  };

  return (
    <CustomSelectChips
      fieldName={fieldName}
      rawSelectedItems={rawSelectedItems as string[] | string}
      error={error}
      touched={touched}
      setFieldValue={setFieldValue}
      {...rest}
      selectProps={{
        ...rest.selectProps,
        onBlur,
      }}
    />
  );
};

const CustomSelectChipsContent: React.FC<ICustomSelectChips> = ({
  rawSelectedItems,
  error,
  touched,
  setFieldValue,
  isViewOnly,
  list,
  fieldName,
  helperText = '',
  selectProps,
  chipsProps,
}) => {
  const selectedItems =
    typeof rawSelectedItems === 'string'
      ? rawSelectedItems.length
        ? [rawSelectedItems]
        : []
      : rawSelectedItems;

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    let value = event.target.value;
    value = Array.isArray(value) ? value : [value];
    setFieldValue(fieldName, value, true);
  };

  const handleDelete = (value: string) => {
    const newSelectedItems = selectedItems.filter((id: string) => id !== value);
    setFieldValue(fieldName, newSelectedItems, true);
  };

  const isMultiple =
    selectProps?.multiple !== undefined ? selectProps.multiple : true;
  const currentValue = isMultiple ? selectedItems || [] : selectedItems;

  return (
    <FormControl
      data-cy="custom-select-chip__container--form-control"
      fullWidth
    >
      <Select
        data-cy="custom-select-chip__select"
        multiple
        value={currentValue}
        fullWidth
        disabled={isViewOnly}
        onChange={handleChange}
        error={touched && !!error}
        name={fieldName}
        {...selectProps}
        renderValue={() => (
          <Grid container spacing={1}>
            {selectedItems.map((value: string) => (
              <Grid item key={value}>
                <Chip
                  data-cy="custom-select-chip__chips--selected-chips"
                  disabled={isViewOnly}
                  variant={'outlined'}
                  label={
                    list.find((item: IOptionData) => item.value === value)
                      ?.label
                  }
                  onDelete={() => handleDelete(value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  clickable
                  color="primary"
                  {...chipsProps}
                />
              </Grid>
            ))}
          </Grid>
        )}
      >
        {list.map((item: IOptionData) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
      {typeof error === 'string' && touched ? (
        <FormHelperText error>
          <ErrorMessage name={fieldName} />
        </FormHelperText>
      ) : (
        helperText &&
        !isViewOnly && (
          <FormHelperText focused>{helperText || ''}</FormHelperText>
        )
      )}
    </FormControl>
  );
};

const CustomSelectChips = React.memo(CustomSelectChipsContent, (prev, next) => {
  const items =
    JSON.stringify(prev.rawSelectedItems) ===
    JSON.stringify(next.rawSelectedItems);
  const touchedError = next.touched
    ? !prev.touched
      ? !next.error
      : prev.error === next.error
    : true;
  return items && touchedError;
});

export default CustomSelectChipsWrapper;
